import { useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FeedbackProvider, FeedbackWidget } from '@pgphox/feedback-react';
import { useAuth } from '../../hooks/useAuth';

// App-wide bug reporter (@pgphox/feedback-react): records the current tab + mic
// plus scrubbed console/network/click logs and files a Jira task via the
// central feedback service. The APP KEY selects the Jira project on the
// service (GreenShield's default key → GP; the PharmAssist instance's key →
// PRX, see docker/uat-stack). Docs: github.com/walidpg/phox-feedback.
//
// Who sees the button is per instance (PRX-29). The default — no
// VITE_FEEDBACK_ROLES — keeps Walid's GreenShield rule: wally_dev_admin ONLY.
// This is dev tooling, not a workflow feature, so the customRole-null graceful
// fallback deliberately does not apply — no role, no button, zero overhead
// (the provider never mounts, so no instrumentation is installed). A pharmacy
// instance sets VITE_FEEDBACK_ROLES to a comma-separated role list, or "*" for
// every signed-in user, so the pharmacy reports straight into its own project.
const SERVICE_URL =
  import.meta.env.VITE_FEEDBACK_SERVICE_URL ||
  'https://feedback-service-106254439960.northamerica-northeast2.run.app';
const APP_KEY = import.meta.env.VITE_FEEDBACK_APP_KEY || 'nks-ef5f1295';
const FEEDBACK_ROLES: string[] = String(import.meta.env.VITE_FEEDBACK_ROLES || 'wally_dev_admin')
  .split(',')
  .map((role) => role.trim())
  .filter(Boolean);
const SHOW_FOR_EVERY_SIGNED_IN_USER = FEEDBACK_ROLES.includes('*');

export function FeedbackCapture() {
  const { user, isAuthenticated } = useAuth0();
  const { hasCustomRole } = useAuth();

  // The capture engine snapshots its config once at mount; reading the user
  // through a ref makes identify() reflect the auth state at SEND time.
  const userRef = useRef(user);
  userRef.current = user;

  if (!isAuthenticated) return null;
  if (
    !SHOW_FOR_EVERY_SIGNED_IN_USER &&
    !FEEDBACK_ROLES.some((role) => hasCustomRole(role as Parameters<typeof hasCustomRole>[0]))
  ) {
    return null;
  }

  return (
    <FeedbackProvider
      config={{
        serviceUrl: SERVICE_URL,
        appKey: APP_KEY,
        appVersion: import.meta.env.MODE,
        identify: () => ({ email: userRef.current?.email, name: userRef.current?.name }),
      }}
    >
      <FeedbackWidget />
    </FeedbackProvider>
  );
}
